import { FirebaseApp, getApps, initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { Auth, UserCredential, connectAuthEmulator, getAuth, signInWithCustomToken } from "firebase/auth";
import { Database, getDatabase } from "firebase/database";
import { connectFirestoreEmulator } from "firebase/firestore";
import { Functions, connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { FirebaseStorage, connectStorageEmulator, getStorage } from "firebase/storage";
import { Firestore, getFirestore, initializeFirestore } from "@firebase/firestore";
import React from "react";

const LOCALHOST = "0.0.0.0";

export interface Firebase {
  instance: FirebaseApp;
  storage: FirebaseStorage;
  auth: Auth;
  db: Database;
  functions: Functions;
  firestore: Firestore;
  pinAuth: (token: string) => Promise<UserCredential>;
}

const getConfig = () => {
  return {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    firestoreUrl: process.env.REACT_APP_FIREBASE_FIRESTORE_URL,
  };
};

// App Check attests that backend calls come from the real app (bot abuse
// mitigation). Without a site key in the environment it is skipped entirely,
// and it stays in monitoring mode until enforcement is turned on in the
// Firebase console.
const initAppCheck = (instance: FirebaseApp) => {
  const siteKey = process.env.REACT_APP_APPCHECK_SITE_KEY;
  if (!siteKey || isEmulationEnabled()) return;

  if (process.env.NODE_ENV === "development") {
    // Local dev against the real backend exchanges a debug token instead of a
    // reCAPTCHA attestation. The token is printed to the browser console on
    // first run and must be registered in Firebase console -> App Check.
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  initializeAppCheck(instance, {
    provider: new ReCaptchaV3Provider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
};

export const initFirebase = () => {
  const config = getConfig();

  const apps = getApps();
  const alreadyInitialized = apps.length > 0;
  const instance = alreadyInitialized ? apps[0] : initializeApp(config);
  if (!alreadyInitialized) initAppCheck(instance);
  const storage = getStorage(instance);
  const db = getDatabase(instance);
  const functions = getFunctions(instance, "europe-west1");
  // Firestore's default WebChannel streaming can hang silently on iOS Safari
  // (listeners attach, nothing ever arrives — infinite loading states). Auto-detect
  // falls back to long polling on such connections. initializeFirestore must run
  // before any getFirestore for settings to apply.
  const firestore = alreadyInitialized
    ? getFirestore(instance)
    : initializeFirestore(instance, { experimentalAutoDetectLongPolling: true });
  const auth = getAuth(instance);

  const emulate = isEmulationEnabled();
  if (emulate && !alreadyInitialized) connectAuthEmulator(auth, `http://${LOCALHOST}:9099`);
  if (emulate && !alreadyInitialized) connectFunctionsEmulator(functions, LOCALHOST, 5001);
  if (emulate && !alreadyInitialized) connectStorageEmulator(storage, LOCALHOST, 9199);
  if (emulate && !alreadyInitialized) connectFirestoreEmulator(firestore, LOCALHOST, 9090);

  const pinAuth = (token: string) => signInWithCustomToken(auth, token);

  return {
    instance,
    storage,
    auth,
    functions,
    db,
    firestore,
    pinAuth,
  };
};

export const FirebaseContext = React.createContext<Firebase>({} as Firebase);

export const isEmulationEnabled = () => process.env.REACT_APP_FIREBASE_EMULATE === "TRUE";
